/**
 * /admin/access-rules (FND-25, SAD-1241) — Admin › Access rules.
 *
 * Every sensitive "who can see / do X" question is one access rule (a Cerbos
 * attribute rule decided by enies-lobby). The rule's shape is code; WHO it
 * lets through is this tenant's setting: a mode from a closed list, the
 * permissions the mode reads, an optional role constraint. Hard denies are
 * shown read-only — no control can change them. The page never decides
 * access itself; it only edits the setting the server validates again.
 *
 * Data: AdminListAccessRules / AdminUpdateAccessRule / AdminResetAccessRule.
 */ import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraBadge, SpecraButton, SpecraCombobox, SpecraDataTable, SpecraEmptyState, SpecraInlineBanner, SpecraOverlayDialog, SpecraPageHeader, SpecraSelect, SpecraTag, SpecraTooltip } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useState } from 'react';
import { useListAccessRules, useResetAccessRule, useUpdateAccessRule } from '../Access/hooks';
import { actionLabel, CAPABILITY_GROUPS } from '../Permissions/vocabulary';
/** Plain labels for the closed mode list (spec FND-25 §10). */ export const MODE_LABELS = {
    nobody: 'Nobody',
    everyone: 'Everyone in scope',
    any_action: 'Anyone with any of…',
    all_actions: 'Anyone with all of…',
    owner_or_any_action: 'Owner or anyone with…',
    record_opt_in: 'Only where the record opts in, for…',
    respect_walls: 'Everyone except people behind a wall, with…',
    service_default: 'As Specra decides by default (membership)'
};
/** Modes that read no permission list (the picker is disabled). */ const MODES_WITHOUT_ACTIONS = new Set([
    'nobody',
    'everyone',
    'service_default'
]);
/** Display label of a platform user role ("admin" → "Admin"). */ function roleLabel(role) {
    return role.charAt(0).toUpperCase() + role.slice(1);
}
/** Fallback vocabulary when the server omits it (an older enies-lobby). */ const FALLBACK_ACTIONS = Array.from(new Set([
    'read',
    ...CAPABILITY_GROUPS.flatMap((g)=>g.items.map((i)=>i.action))
]));
const FALLBACK_ROLES = [
    'admin',
    'internal',
    'member'
];
/**
 * Picker options. The server sends the exact canonical vocabulary it accepts
 * (AdminListAccessRules.available_actions / available_roles), so every valid
 * setting is reachable; labels come from the permissions vocabulary.
 */ export function actionOptions(actions) {
    return (actions.length ? actions : FALLBACK_ACTIONS).map((a)=>({
            value: a,
            label: actionLabel(a),
            textValue: `${actionLabel(a)} ${a}`
        }));
}
export function roleOptions(roles) {
    return (roles.length ? roles : FALLBACK_ROLES).map((r)=>({
            value: r,
            label: roleLabel(r)
        }));
}
/** What a reset restores: mode, its permissions and any role limit. */ export function resetDescription(rule) {
    const mode = MODE_LABELS[rule.defaultMode] ?? rule.defaultMode;
    const actions = rule.defaultActions.length ? `: ${rule.defaultActions.map(actionLabel).join(', ')}` : '';
    const roles = rule.defaultRoles.length ? `, limited to ${rule.defaultRoles.map(roleLabel).join(', ')}` : '';
    return `"${rule.question}" goes back to ${mode}${actions}${roles}.`;
}
function draftOf(rule) {
    return {
        mode: rule.mode,
        actions: [
            ...rule.actions
        ],
        roles: [
            ...rule.roles
        ]
    };
}
function sameList(a, b) {
    return a.length === b.length && a.every((v, i)=>v === b[i]);
}
function isDirty(rule, d) {
    if (!d) return false;
    return d.mode !== rule.mode || !sameList(d.actions, rule.actions) || !sameList(d.roles, rule.roles);
}
function formatDate(rule) {
    const ts = rule.updatedAt;
    if (!ts) return '';
    const d = new Date(Number(ts.seconds) * 1000);
    return d.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
    });
}
function ruleTestId(base, key) {
    return `${base}-${key}`;
}
function StatusCell({ rule }) {
    return /*#__PURE__*/ _jsxs("div", {
        className: "flex flex-wrap items-center gap-[var(--space-1)]",
        children: [
            rule.customised ? /*#__PURE__*/ _jsxs(SpecraBadge, {
                variant: "info",
                "data-testid": ruleTestId(Testids.accessRules.status, rule.key),
                children: [
                    "Changed by ",
                    rule.updatedByName || rule.updatedBy,
                    formatDate(rule) ? `, ${formatDate(rule)}` : ''
                ]
            }) : /*#__PURE__*/ _jsx(SpecraBadge, {
                variant: "neutral",
                "data-testid": ruleTestId(Testids.accessRules.status, rule.key),
                children: "Default"
            }),
            rule.recordAudience && /*#__PURE__*/ _jsx(SpecraTooltip, {
                content: "Each record's author chooses who sees it, within this rule. Authors always see their own.",
                children: /*#__PURE__*/ _jsx(SpecraBadge, {
                    variant: "neutral",
                    tabIndex: 0,
                    children: "Ceiling"
                })
            })
        ]
    });
}
function ListPicker({ label, placeholder, values, options, disabled, testId, onChange }) {
    const remaining = options.filter((o)=>!values.includes(o.value));
    const labelOf = (v)=>options.find((o)=>o.value === v)?.label ?? v;
    return /*#__PURE__*/ _jsxs("div", {
        className: "flex min-w-48 flex-col gap-[var(--space-1)]",
        children: [
            values.length > 0 && /*#__PURE__*/ _jsx("div", {
                className: "flex flex-wrap gap-[var(--space-1)]",
                children: values.map((v)=>/*#__PURE__*/ _jsx(SpecraTag, {
                        onRemove: disabled ? undefined : ()=>onChange(values.filter((x)=>x !== v)),
                        removeLabel: `Remove ${labelOf(v)}`,
                        children: labelOf(v)
                    }, v))
            }),
            /*#__PURE__*/ _jsx(SpecraCombobox, {
                label: label,
                placeholder: values.length > 0 ? 'Add…' : placeholder,
                searchPlaceholder: "Search…",
                options: remaining,
                value: undefined,
                disabled: disabled,
                onValueChange: (v)=>{
                    if (v) onChange([
                        ...values,
                        v
                    ]);
                },
                triggerProps: {
                    'data-testid': testId
                }
            })
        ]
    });
}
export function AccessRulesPage() {
    const { data, isLoading, error } = useListAccessRules();
    const rules = data?.rules;
    const actionChoices = actionOptions(data?.availableActions ?? []);
    const roleChoices = roleOptions(data?.availableRoles ?? []);
    const update = useUpdateAccessRule();
    const reset = useResetAccessRule();
    const [drafts, setDrafts] = useState({});
    const [rowErrors, setRowErrors] = useState({});
    const [confirmReset, setConfirmReset] = useState(null);
    const setDraft = (rule, patch)=>setDrafts((prev)=>({
                ...prev,
                [rule.key]: {
                    ...prev[rule.key] ?? draftOf(rule),
                    ...patch
                }
            }));
    const clearRow = (key)=>{
        setDrafts(({ [key]: _, ...rest })=>rest);
        setRowErrors(({ [key]: _, ...rest })=>rest);
    };
    const save = (rule)=>{
        const d = drafts[rule.key];
        if (!d) return;
        const actions = MODES_WITHOUT_ACTIONS.has(d.mode) ? [] : d.actions;
        update.mutate({
            key: rule.key,
            mode: d.mode,
            actions,
            roles: d.roles
        }, {
            onSuccess: ()=>clearRow(rule.key),
            onError: (err)=>setRowErrors((prev)=>({
                        ...prev,
                        [rule.key]: err instanceof Error ? err.message : 'Could not save this rule'
                    }))
        });
    };
    // Rebuilt each render: the cells read the row drafts and errors.
    const columns = [
        {
            id: 'question',
            header: 'Question',
            width: '22%',
            cell: (rule)=>/*#__PURE__*/ _jsxs("div", {
                    className: "flex flex-col gap-[var(--space-1)]",
                    children: [
                        /*#__PURE__*/ _jsx("span", {
                            className: "text-foreground",
                            children: rule.question
                        }),
                        /*#__PURE__*/ _jsx("span", {
                            className: "font-mono text-[length:var(--fs-ui-caption)] text-muted-foreground",
                            children: rule.key
                        }),
                        rowErrors[rule.key] && /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                            tone: "danger",
                            "data-testid": ruleTestId(Testids.accessRules.error, rule.key),
                            children: rowErrors[rule.key]
                        })
                    ]
                })
        },
        {
            id: 'who',
            header: 'Who',
            width: '20%',
            cell: (rule)=>{
                const d = drafts[rule.key] ?? draftOf(rule);
                return /*#__PURE__*/ _jsx(SpecraSelect, {
                    size: "sm",
                    className: "min-w-56",
                    "aria-label": `Who: ${rule.question}`,
                    "data-testid": ruleTestId(Testids.accessRules.modeSelect, rule.key),
                    value: d.mode,
                    onChange: (e)=>{
                        const mode = e.target.value;
                        setDraft(rule, {
                            mode,
                            actions: MODES_WITHOUT_ACTIONS.has(mode) ? [] : d.actions.length ? d.actions : [
                                ...rule.defaultActions
                            ]
                        });
                    },
                    children: rule.allowedModes.map((m)=>/*#__PURE__*/ _jsx("option", {
                            value: m,
                            children: MODE_LABELS[m] ?? m
                        }, m))
                });
            }
        },
        {
            id: 'permissions',
            header: 'Permissions',
            width: '20%',
            cell: (rule)=>{
                const d = drafts[rule.key] ?? draftOf(rule);
                return /*#__PURE__*/ _jsx(ListPicker, {
                    label: `Permissions: ${rule.question}`,
                    placeholder: "Choose permissions",
                    values: MODES_WITHOUT_ACTIONS.has(d.mode) ? [] : d.actions,
                    options: actionChoices,
                    disabled: MODES_WITHOUT_ACTIONS.has(d.mode),
                    testId: ruleTestId(Testids.accessRules.actionsSelect, rule.key),
                    onChange: (actions)=>setDraft(rule, {
                            actions
                        })
                });
            }
        },
        {
            id: 'roles',
            header: 'Roles',
            width: '14%',
            cell: (rule)=>{
                const d = drafts[rule.key] ?? draftOf(rule);
                return /*#__PURE__*/ _jsx(ListPicker, {
                    label: `Roles: ${rule.question}`,
                    placeholder: "Any role",
                    values: d.roles,
                    options: roleChoices,
                    testId: ruleTestId(Testids.accessRules.rolesSelect, rule.key),
                    onChange: (roles)=>setDraft(rule, {
                            roles
                        })
                });
            }
        },
        {
            id: 'denied',
            header: 'Always denied',
            width: '12%',
            cell: (rule)=>rule.hardDenies.length ? /*#__PURE__*/ _jsx("div", {
                    className: "flex flex-wrap gap-[var(--space-1)]",
                    children: rule.hardDenies.map((h)=>/*#__PURE__*/ _jsx(SpecraBadge, {
                            variant: "neutral",
                            children: h
                        }, h))
                }) : /*#__PURE__*/ _jsx("span", {
                    className: "text-muted-foreground",
                    children: "—"
                })
        },
        {
            id: 'status',
            header: 'Status',
            width: '12%',
            cell: (rule)=>/*#__PURE__*/ _jsx(StatusCell, {
                    rule: rule
                })
        }
    ];
    return /*#__PURE__*/ _jsxs("div", {
        className: "flex flex-col gap-[var(--space-4)]",
        "data-testid": Testids.accessRules.page,
        children: [
            /*#__PURE__*/ _jsx(SpecraPageHeader, {
                title: "Access rules",
                description: "Who can see or do each sensitive thing. Defaults match how Specra behaved before; changes apply within a minute."
            }),
            error ? /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                tone: "danger",
                children: error instanceof Error ? error.message : 'Could not load access rules'
            }) : !isLoading && (rules ?? []).length === 0 ? /*#__PURE__*/ _jsx(SpecraEmptyState, {
                title: "No access rules",
                description: "This deployment's catalogue has no access rules yet."
            }) : /*#__PURE__*/ _jsx(SpecraDataTable, {
                caption: "Access rules",
                columns: columns,
                rows: rules ?? [],
                loading: isLoading,
                loadingLabel: "Loading access rules",
                getRowId: (rule)=>rule.key,
                getRowProps: (rule)=>({
                        'data-item-id': rule.key,
                        'data-testid': Testids.accessRules.row
                    }),
                actionsLabel: "Actions",
                rowActions: (rule)=>/*#__PURE__*/ _jsxs("div", {
                        className: "flex items-center gap-[var(--space-1)]",
                        children: [
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                size: "sm",
                                disabled: !isDirty(rule, drafts[rule.key]) || update.isPending,
                                onClick: ()=>save(rule),
                                "data-testid": ruleTestId(Testids.accessRules.save, rule.key),
                                children: "Save"
                            }),
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                size: "sm",
                                variant: "ghost",
                                disabled: !rule.customised || reset.isPending,
                                onClick: ()=>setConfirmReset(rule),
                                "data-testid": ruleTestId(Testids.accessRules.reset, rule.key),
                                children: "Reset to default"
                            })
                        ]
                    })
            }),
            /*#__PURE__*/ _jsx(SpecraOverlayDialog, {
                open: confirmReset !== null,
                onOpenChange: (open)=>{
                    if (!open) setConfirmReset(null);
                },
                size: "sm",
                title: "Reset to default?",
                description: confirmReset ? resetDescription(confirmReset) : undefined,
                footer: /*#__PURE__*/ _jsxs("div", {
                    className: "flex justify-end gap-[var(--space-2)]",
                    children: [
                        /*#__PURE__*/ _jsx(SpecraButton, {
                            variant: "secondary",
                            onClick: ()=>setConfirmReset(null),
                            children: "Cancel"
                        }),
                        /*#__PURE__*/ _jsx(SpecraButton, {
                            "data-testid": Testids.accessRules.resetConfirm,
                            disabled: reset.isPending,
                            onClick: ()=>{
                                if (!confirmReset) return;
                                const key = confirmReset.key;
                                reset.mutate(key, {
                                    onSuccess: ()=>{
                                        clearRow(key);
                                        setConfirmReset(null);
                                    },
                                    onError: (err)=>{
                                        setRowErrors((prev)=>({
                                                ...prev,
                                                [key]: err instanceof Error ? err.message : 'Could not reset this rule'
                                            }));
                                        setConfirmReset(null);
                                    }
                                });
                            },
                            children: "Reset to default"
                        })
                    ]
                }),
                children: /*#__PURE__*/ _jsx("p", {
                    className: "text-[length:var(--fs-ui-body)] text-muted-foreground",
                    children: "The tenant setting is removed; future changes to the default apply automatically."
                })
            })
        ]
    });
}
